import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { format, parse } from "date-fns";
import { useFragment } from "react-relay";

import {
  CalendarFilledIcon,
  ClockIcon,
  PlaceIcon,
  PersonFilledIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { VStack } from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { useJoinableSpots } from "../../hooks/useJoinableSpots";
import { InviteFullMessage } from "../InviteFullMessage/InviteFullMessage";
import { InfoLine } from "./InfoLine/InfoLine";
import { InviteCardBookingInfoSection_Booking$key } from "./__generated__/InviteCardBookingInfoSection_Booking.graphql";

export const InviteCardBookingInfoSection = ({
  bookingRef,
}: {
  bookingRef: InviteCardBookingInfoSection_Booking$key | null;
}) => {
  const data = useFragment(
    InviteCardBookingInfoSectionFragmentString,
    bookingRef
  );
  const { hasJoinableSpots, joinableSpotCount } = useJoinableSpots(
    data ?? null
  );

  //Date
  const rawDate = data?.startSlot?.agenda?.date;
  let dateObj = null;
  let dateString = "";
  if (rawDate) {
    try {
      dateObj = parse(rawDate, "yyyy-MM-dd", new Date());
      dateString = format(dateObj, "EEE, MMM do");
    } catch (error) {
      dateString = rawDate; // Fallback to raw date if parsing fails
    }
  }

  //Time
  const rawTime = data?.startSlot?.time;
  let timeObj = null;
  let timeString = "";
  const timeFormatString = `h:mm aa`;
  if (rawTime) {
    try {
      timeObj = parse(rawTime, "HH:mm:ss", new Date());
      timeString = format(timeObj, timeFormatString);
    } catch (error) {
      timeString = rawTime; // Fallback to raw time if parsing fails
    }
  }

  //Location
  const shouldShowLocation =
    (data?.startSlot?.agenda?.facility?.courses?.count ?? 0) > 1;
  const locationCopyOverride = getNounOverride(
    data?.startSlot?.agenda?.facility?.copyOverrides?.course?.singular,
    t`Course`
  );
  const locationName = data?.startSlot?.course?.name ?? "";

  return hasJoinableSpots ? (
    <VStack backgroundColor="surface-primary" padding={6} gap={2}>
      <InfoLine
        icon={<CalendarFilledIcon />}
        label={t`Date`}
        value={dateString}
      />
      <InfoLine icon={<ClockIcon />} label={t`Time`} value={timeString} />
      {shouldShowLocation ? (
        <InfoLine
          icon={<PlaceIcon />}
          label={locationCopyOverride}
          value={locationName}
        />
      ) : null}
      <InfoLine
        icon={<PersonFilledIcon />}
        label={t`Available Spots`}
        value={joinableSpotCount.toString()}
      />
    </VStack>
  ) : (
    <InviteFullMessage />
  );
};

const InviteCardBookingInfoSectionFragmentString = graphql`
  fragment InviteCardBookingInfoSection_Booking on Booking {
    ...useJoinableSpots_Booking
    startSlot {
      agenda {
        date
        facility {
          courses {
            count
          }
          copyOverrides {
            course {
              singular
            }
          }
        }
      }
      ... on TimeSlot {
        time
        course {
          name
        }
      }
    }
  }
`;
